import {Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme, Chip, CircularProgress} from "@mui/material";
import React, { useState, useEffect } from "react";
import {ExternalCompanyDto} from "../../shared/dtos/company.dto";
import {StoredImageDto} from "../../shared/dtos/stored-image.dto";
import {formatTreeOperationOneLine} from "../../shared/services/tree-helpers.service";
import {ItemizedEstimateDto} from "../../shared/dtos/itemized-estimate.dto";
import {PhotoViewer} from "../../shared/components/PhotoViewer";
import { EstimateTreeDto } from "../../shared/dtos/estimate-tree.dto";
import { EstimateTreeOperationDto } from "../../shared/dtos/estimate-tree-operation.dto";
import { STEP_WALL_MINUTES, effectiveLaborMinutes, formatMinutes, wallClockMinutes } from "../../shared/services/operation-time.service";
import { lockedCost } from "../../shared/pricing/equipment-charges";

const money = (value: number) => new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(value);
const hoursAndMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? (m > 0 ? `${h} h ${m} min` : `${h} h`) : `${m} min`;
};

export type EstimateItemizedDisplayType = 'estimate' | 'invoice' | 'receipt' | 'work-order';
export type EstimateItemizedDisplayMode = 'admin' | 'customer' | 'contractor';

const abbreviateUnits = (value: string | undefined): string => {
  if (!value) return '';
  return value
    .replace(/\bfeet\b/gi, 'ft')
    .replace(/\bfoot\b/gi, 'ft')
    .replace(/\binches\b/gi, 'in')
    .replace(/\binch\b/gi, 'in');
};

const formatWorkItemHeader = (tree: EstimateTreeDto, index: number): { title: string; details: string } => {
  const workItemNumber = `Work Item ${index + 1}`;
  
  switch (tree.workItemType) {
    case 'Tree':
      return {
        title: workItemNumber,
        details: `Tree • ${tree.species || 'Unknown'} • Height: ${abbreviateUnits(tree.height) || 'N/A'} • Width: ${abbreviateUnits(tree.width) || 'N/A'} • Trunk Diameter: ${abbreviateUnits(tree.trunkDiameter) || 'N/A'}`
      };
      
    case 'TreeGroup':
      const treeCount = tree.additionalDetails?.TreeGroup_treeCount || 0;
      return {
        title: workItemNumber,
        details: `Tree Group (${treeCount} trees) • ${tree.species || 'Unknown'} • Avg Height: ${abbreviateUnits(tree.height) || 'N/A'} • Avg Width: ${abbreviateUnits(tree.width) || 'N/A'} • Avg Trunk Diameter: ${abbreviateUnits(tree.trunkDiameter) || 'N/A'}`
      };
      
    case 'FallenTree':
      return {
        title: workItemNumber,
        details: `Fallen Tree • ${tree.species || 'Unknown'} • Height: ${abbreviateUnits(tree.height) || 'N/A'} • Width: ${abbreviateUnits(tree.width) || 'N/A'} • Trunk Diameter: ${abbreviateUnits(tree.trunkDiameter) || 'N/A'}`
      };
      
    case 'DebrisPile':
      const debrisPileLength = tree.additionalDetails?.DebrisPile_length;
      return {
        title: workItemNumber,
        details: `Debris Pile • ${tree.species || 'Unknown'} • Height: ${abbreviateUnits(tree.height) || 'N/A'} • Width: ${abbreviateUnits(tree.width) || 'N/A'} • Length: ${debrisPileLength ? `${debrisPileLength} ft` : 'N/A'}`
      };
      
    case 'Stump':
      // The stump's diameter is the work item's (SQ-630); "0 Inches" is an unpicked size.
      const stumpDiameter = /^0+(\.0+)?( Inches)?$/i.test((tree.trunkDiameter ?? '').trim()) ? undefined : abbreviateUnits(tree.trunkDiameter);
      return {
        title: workItemNumber,
        details: `Stump • ${tree.species || 'Unknown'} • Stump Diameter: ${stumpDiameter || 'N/A'}`
      };
      
    case 'Misc':      
      return {
        title: workItemNumber,
        details: 'Misc'
      };
      
    default:
      return {
        title: workItemNumber,
        details: tree.workItemType
      };
  }
};

type AiStatus = 'NotStarted' | 'InProgress' | 'Completed' | 'Failed';

const getStatusChipColor = (status: AiStatus) => {
  switch (status) {
    case 'NotStarted': return 'default';
    case 'InProgress': return 'warning';
    case 'Completed': return 'success';
    case 'Failed': return 'error';
    default: return 'default';
  }
};

const getStatusDisplayText = (status: AiStatus) => {
  switch (status) {
    case 'NotStarted': return 'AI: Not Started';
    case 'InProgress': return 'AI: In Progress';
    case 'Completed': return 'AI: Completed';
    case 'Failed': return 'AI: Failed';
    default: return status;
  }
};

const PriceDisplay = ({ mode, value }: {
  mode: 'read-only' | 'hidden',
  value: number,
}) => {
  if(mode === 'hidden') {
    return <Box></Box>
  }
  return <span>${value}</span>;
};

/**
 * AC-267 time-first editing (Android stepper parity): the stepper moves
 * wall-clock time in 15-minute taps; the price is derived server-side from
 * the resulting labor minutes and displayed read-only.
 */
const TimeStepperPrice = ({ laborMinutes, crewSize, noCrew = false, price, onStep, variant = 'body1', loading = false, aiStatus }: {
  laborMinutes: number,
  crewSize: number,
  /** SQ-559: no crew on the record: the time reads "—" (price unchanged) and nothing steps. */
  noCrew?: boolean,
  price: number,
  onStep: (deltaWallMinutes: number) => void,
  variant?: 'body1' | 'h6',
  loading?: boolean,
  aiStatus?: AiStatus
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ minWidth: '100px' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const stepButtonSx = { minWidth: '32px', padding: '2px 6px' };
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
        <Button size="small" variant="outlined" sx={stepButtonSx} disabled={noCrew} onClick={() => onStep(-STEP_WALL_MINUTES)}>−</Button>
        <Typography variant="body1" sx={{ minWidth: '56px', textAlign: 'center' }}>
          {noCrew ? '—' : formatMinutes(wallClockMinutes(laborMinutes, crewSize))}
        </Typography>
        <Button size="small" variant="outlined" sx={stepButtonSx} disabled={noCrew} onClick={() => onStep(STEP_WALL_MINUTES)}>+</Button>
        <Typography
          variant={variant}
          sx={{ minWidth: '70px', textAlign: 'right', fontWeight: variant === 'h6' ? 'bold' : 'normal' }}
        >
          ${price}
        </Typography>
      </Box>
      {aiStatus && (
        <Chip
          label={getStatusDisplayText(aiStatus)}
          color={getStatusChipColor(aiStatus)}
          size="small"
          variant="outlined"
        />
      )}
    </Box>
  );
};

export interface EstimateItemizedStatelessProps {
  company: ExternalCompanyDto;
  client: {name: string, address: string, email?: string, phoneNumber?: string},
  estimate: ItemizedEstimateDto;
  displayType: EstimateItemizedDisplayType;
  displayMode: EstimateItemizedDisplayMode;
  editable: boolean;
  displayNameAndDate: string;
  id?: string;
  // Time-first editing (AC-267): steppers emit wall-clock minute deltas; the
  // pages turn them into minutes-only bulk PATCHes and the server derives
  // every price.
  onOperationTimeStep?: (treeId: number, operationId: number, deltaWallMinutes: number) => void;
  onTreeTimeStep?: (treeId: number, deltaWallMinutes: number) => void;
  onTotalTimeStep?: (deltaWallMinutes: number) => void;
  /** Estimate crew size; wall-clock display = labor minutes ÷ crew. */
  crewSize?: number;
  /**
   * SQ-559: how many crew members the record has (the job's crew once
   * approved: the DTO's crewCount). 0 shows every time as "—"; the server's
   * crewSize is never below 1, so it cannot say this. Undefined: unknown.
   */
  crewCount?: number;
  /** Needed to seed legacy cost-only rows; steppers render once it loads. */
  billedHourlyLaborRate?: number;
  isLoading?: boolean;
  style?: React.CSSProperties;
}

export const EstimateItemizedStateless = React.forwardRef<HTMLDivElement, EstimateItemizedStatelessProps>((props: EstimateItemizedStatelessProps, ref) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string>();
  const [companyBackgroundUrl, setCompanyBackgroundUrl] = useState<string>(); 

  const onOperationTimeStep = props.onOperationTimeStep ?? (() => {});
  const onTreeTimeStep = props.onTreeTimeStep ?? (() => {});
  const onTotalTimeStep = props.onTotalTimeStep ?? (() => {});
  const crewSize = props.crewSize ?? 1;
  const noCrew = props.crewCount === 0;

  const getItemizedType = () => {
    if (props.displayMode === 'contractor') {
      return 'Job';
    }
    switch (props.displayType) {
      case 'estimate': return 'Estimate';
      case 'invoice': return 'Invoice';
      case 'receipt': return 'Receipt';
      case 'work-order': return 'Job';
    }
  }

  useEffect(() => {
    console.log('EstimateItemizedStateless editable', props.editable, props.displayMode);
  }, [props.editable, props.displayMode]);

  useEffect(() => {
    const loadCompanyLogoUrl = () => {
      const logoUrl = `${process.env.REACT_APP_PUBLIC_ASSETS_BASE_URL}/companies/${props.company.id}/assets/logo.png`;
      setCompanyLogoUrl(logoUrl);
    };

    const loadCompanyBackgroundUrl = async () => {
      const backgroundUrl = `${process.env.REACT_APP_PUBLIC_ASSETS_BASE_URL}/companies/${props.company.id}/assets/invoice_background.png`;
      setCompanyBackgroundUrl(backgroundUrl);
    };

    loadCompanyLogoUrl();
    loadCompanyBackgroundUrl();
  }, [props.company.id]);

  const calculateTotalCost = (trees: EstimateTreeDto[]) =>
    trees.flatMap(t => t.operations).reduce((acc, operation) => acc + (operation.estimatedCost ?? 0), 0);

  const rate = props.billedHourlyLaborRate;
  const calculateTotalLaborMinutes = (trees: EstimateTreeDto[]) =>
    rate == null ? 0 : trees.flatMap(t => t.operations)
      .reduce((acc, operation) => acc + effectiveLaborMinutes(operation as EstimateTreeOperationDto, rate), 0);

  const getAllImages = (tree: EstimateTreeDto) =>
    [...tree.images];

  const getTreeGates = (tree: EstimateTreeDto) => (props.estimate?.gates ?? []).filter(gate =>
    gate.associatedTrees.some(t => t.id === tree.id)
  );

  // SQ-572: a click opens the photo full screen, zoomable, with its
  // neighbours (the work item's pictures, or that gate's) a swipe away.
  const [viewing, setViewing] = useState<{photos: StoredImageDto[], index: number} | null>(null);
  const onImageClick = (photos: StoredImageDto[], image: StoredImageDto) =>
    setViewing({photos, index: Math.max(0, photos.findIndex(p => p.id === image.id))});

  // SQ-636: the server hid this record's prices from the person (pricesMasked): no figure anywhere, no
  // machine time, no Pricing section, no Total row, and none of the pricing rules' notes (they are hidden
  // too, so the record would read as predating them).
  const pricesHidden = props.estimate.pricesMasked === true;
  const pricingShown = props.displayMode === 'admin' && !pricesHidden;
  const lineItemPriceHidden = props.displayMode !== 'admin' || pricesHidden;
  const totalPriceHidden = props.displayMode === 'contractor';
  const pricesEditable = props.displayMode === 'admin' && props.editable;
  const lineItemCostInputMode = lineItemPriceHidden ? 'hidden' : pricesEditable ? 'editable' : 'read-only';
  const totalCostInputMode = totalPriceHidden ? 'hidden' : pricesEditable ? 'editable' : 'read-only';

  return (
    <div ref={ref}>
      <Paper sx={{
        padding: 2,
        margin: 'auto',
        maxWidth: '800px',
        position: 'relative',
        overflow: 'hidden',
        // Use the ::before pseudo-element to apply background with opacity
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.1)', // Change this to your desired color
          zIndex: 0,
        },
        // Ensure that the Paper content is positioned above the ::before background
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
      }} style={{...props.style}} id={props.id}>
        <div style={{
          pointerEvents: 'none',
          position: 'absolute',
          width: '100%',
          height: '100%',
          left: 0,
          top: 0,
          backgroundImage: `url(${companyBackgroundUrl})`,
          backgroundSize: '800px auto',
          backgroundRepeat: 'repeat-y',
          opacity: 0.1}}>
        </div>
        <div style={{opacity: 1}}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              {isSmallScreen && <img src={companyLogoUrl} alt="Company Logo" style={{width: 150}}/>}
              <Typography variant="h3" gutterBottom>
                {props.company.name} {getItemizedType()}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {props.displayNameAndDate}
              </Typography>
            </Box>
            {!isSmallScreen && <img src={companyLogoUrl} alt="Company Logo" style={{width: 150}}/>}
          </Box>
          <Typography variant="h6" gutterBottom>Client</Typography>
          <Typography variant="body1" gutterBottom>{props.client.name}</Typography>
          <Typography variant="body1" gutterBottom>{props.client.address}</Typography>
          <Typography variant="body1" gutterBottom>{props.client.email}</Typography>
          <Typography variant="body1" gutterBottom>{props.client.phoneNumber}</Typography>
          <TableContainer>
            <Table aria-label="invoice table" >
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontSize: '1.4rem' }}>Service</TableCell>
                  <TableCell align="right" style={{ fontSize: '1.4rem' }}>{pricingShown ? 'Cost' : ''}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.estimate.trees.map((tree, index) => {
                  const workItemHeader = formatWorkItemHeader(tree, index);
                  return (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell component="th" scope="row" style={{ fontSize: '1.2rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                            {workItemHeader.title}
                          </div>
                          <div style={{ fontSize: '0.95rem'}}>
                            {workItemHeader.details}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell align="right" style={{ fontSize: '1.2rem' }}>
                          {lineItemCostInputMode === 'editable' && (rate != null || noCrew) ? (
                            <TimeStepperPrice
                              laborMinutes={calculateTotalLaborMinutes([tree])}
                              crewSize={crewSize}
                              noCrew={noCrew}
                              price={calculateTotalCost([tree])}
                              onStep={(delta) => onTreeTimeStep(tree.id, delta)}
                              variant="h6"
                              loading={props.isLoading}
                            />
                          ) : (
                            <PriceDisplay
                              mode={lineItemCostInputMode === 'hidden' ? 'hidden' : 'read-only'}
                              value={calculateTotalCost([tree])}
                            />
                          )}
                      </TableCell>
                    </TableRow>
                    {tree.operations.map((operation, opIndex) => (
                      <TableRow key={opIndex}>
                        <TableCell style={{ paddingLeft: 40 }}>{formatTreeOperationOneLine(operation)}</TableCell>
                        <TableCell align="right">
                          {lineItemCostInputMode === 'editable' && (rate != null || noCrew) ? (
                            <TimeStepperPrice
                              laborMinutes={rate == null ? 0 : effectiveLaborMinutes(operation, rate)}
                              crewSize={crewSize}
                              noCrew={noCrew}
                              price={operation.estimatedCost ?? 0}
                              onStep={(delta) => onOperationTimeStep(tree.id, operation.id, delta)}
                              variant="h6"
                              loading={props.isLoading}
                              aiStatus={operation.aiEstimatedCostGenerationStatus}
                            />
                          ) : (
                            <PriceDisplay
                              mode={lineItemCostInputMode === 'hidden' ? 'hidden' : 'read-only'}
                              value={operation.estimatedCost ?? 0}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* SQ-441/SQ-523: the machines on the work item, priced from the rate and time
                        locked on the row (the estimate_equipment_cost view's rule). Internal, like
                        line-item prices. A machine at no billed time is listed but adds nothing. */}
                    {pricingShown && (tree.equipment ?? []).length > 0 && (
                      <TableRow>
                        <TableCell style={{ paddingLeft: 40, paddingTop: 4, paddingBottom: 4, borderBottom: 'none', fontSize: '0.9rem' }}>
                          <strong>Equipment</strong>
                        </TableCell>
                        <TableCell style={{ borderBottom: 'none' }}/>
                      </TableRow>
                    )}
                    {pricingShown && (tree.equipment ?? []).map(machine => {
                      const cost = lockedCost({
                        equipmentId: machine.equipmentId, durationMinutes: machine.durationMinutes, hourlyRate: machine.hourlyRate,
                        minimumBillableMinutes: machine.minimumBillableMinutes, minimumCharge: machine.minimumCharge ?? null, wholeDollars: machine.wholeDollars,
                      });
                      const billed = machine.durationMinutes > 0
                        ? `${hoursAndMinutes(machine.durationMinutes)}${machine.hourlyRate != null ? ` at ${money(machine.hourlyRate)}/h` : ''}`
                        : 'no billed time';
                      const actual = machine.actualDurationMinutes != null && machine.actualDurationMinutes !== machine.durationMinutes
                        ? ` · actual ${hoursAndMinutes(machine.actualDurationMinutes)}` : '';
                      return (
                        <TableRow key={`equipment-${machine.id}`}>
                          <TableCell style={{ paddingLeft: 56, paddingTop: 4, paddingBottom: 4, fontSize: '0.9rem' }}>
                            {machine.equipmentNickname ?? machine.equipmentType ?? 'Equipment'} · {billed}{actual}
                            {machine.minimumCharge != null ? ` · ${money(machine.minimumCharge)} minimum` : ''}
                            {machine.rateChanged ? ' · rate changed since' : ''}
                          </TableCell>
                          <TableCell align="right" style={{ fontSize: '0.9rem' }}>
                            <PriceDisplay mode="read-only" value={cost} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell component="th" scope="row" style={{ fontSize: '1.2rem', border: 'none', paddingBottom: '0' }} colSpan={2}>
                        Pictures:
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingLeft: 40, paddingTop: 10, borderBottom: 'none' }} colSpan={2}>
                        {getAllImages(tree).map(image =>
                          <img key={image.id} alt={`Work item ${image.id}`} onClick={() => {onImageClick(getAllImages(tree), image)}} src={image.imageUrl} style={{ width: '100%', maxWidth: 200, marginRight: '10px', cursor: 'pointer'}}/>
                        )}
                      </TableCell>
                    </TableRow>
                    {props.displayMode !== 'customer' && getTreeGates(tree).length > 0 && <>
                      <TableRow>
                        <TableCell component="th" scope="row" style={{ fontSize: '1.2rem', border: 'none', paddingBottom: '0' }} colSpan={2}>
                          Gates:
                        </TableCell>
                      </TableRow>
                      {getTreeGates(tree).map((gate, index) => (
                        <TableRow key={gate.id}>
                          <TableCell style={{ paddingLeft: 40, borderBottom: 'none' }} colSpan={2}>
                            <Typography variant={'h6'}>
                              {`Gate #${index + 1} (${gate.width ?? 'Unknown'}")`}
                            </Typography>
                            {gate.images.map(image =>
                              <img key={image.id} alt={`Gate ${image.id}`} onClick={() => {onImageClick(gate.images, image)}} src={image.imageUrl} style={{ width: '100%', maxWidth: 200, marginRight: '10px', cursor: 'pointer'}}/>
                            )}
                          </TableCell>
                        </TableRow>)
                      )}
                    </>}
                    {!!tree.notes &&
                    <>
                      <TableRow>
                        <TableCell style={{ paddingTop: 20, borderBottom: 'none' }} colSpan={2}>
                          <Typography variant="h6">
                              Notes:
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingLeft: 40, paddingTop: '0', borderBottom: 'none' }} colSpan={2}>
                          <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                            {tree.notes}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </>}
                    {!!tree.crewNotes && props.displayMode !== 'customer' &&
                    <>
                      <TableRow>
                        <TableCell style={{ paddingTop: 20, borderBottom: 'none' }} colSpan={2}>
                          <Typography variant="h6">
                              Crew Notes:
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingLeft: 40, paddingTop: '0', borderBottom: 'none' }} colSpan={2}>
                          <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                            {tree.crewNotes}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </>}
                    {props.displayMode !== 'customer' && ((tree.operationMethods && tree.operationMethods.length > 0) || tree.dragDistanceFeet) && (
                      <TableRow>
                        <TableCell style={{ paddingLeft: 40, paddingTop: 10, fontSize: '0.9rem', borderBottom: 'none' }} colSpan={2}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {tree.operationMethods && tree.operationMethods.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                                <strong>Operation Methods:</strong>
                                {tree.operationMethods.map((method, methodIndex) => (
                                  <span key={methodIndex}>
                                    {methodIndex > 0 && '•'} {method.customOperationName}
                                  </span>
                                ))}
                              </div>
                            )}
                            {tree.dragDistanceFeet !== undefined && tree.dragDistanceFeet !== null && (
                              <div>
                                <strong>Drag Distance:</strong> {Math.round(tree.dragDistanceFeet)} ft
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow >
                      <TableCell style={{ padding: 0, borderBottom: 'none' }} colSpan={2}>
                        <div style={{ height: '10px', backgroundColor: '#818181', borderRadius: '3px'}}/>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                  );
                })}
                {/* The pricing components behind the total (SQ-283 / SQ-441 / SQ-522), internal only:
                    the crew and the rate its labor is priced at, then labor, equipment and cost
                    items as the sums the server totals. The rows below them are what the rules add. */}
                {pricingShown && (props.estimate.laborCost != null || props.estimate.equipmentCost != null) && (
                  <TableRow>
                    <TableCell component="th" scope="row" style={{ fontSize: '1.2rem', borderBottom: 'none', paddingBottom: 0 }} colSpan={2}>
                      Pricing
                    </TableCell>
                  </TableRow>
                )}
                {pricingShown && (props.estimate.crewMembers ?? []).length > 0 && (
                  <TableRow>
                    <TableCell style={{ paddingLeft: 40, fontSize: '0.9rem', borderBottom: 'none' }} colSpan={2}>
                      <strong>Crew:</strong> {(props.estimate.crewMembers ?? []).map(member =>
                        `${member.name}${member.hourlyRate != null ? ` (${money(member.hourlyRate)}/h)` : ''}`).join(', ')}
                      {props.estimate.laborRatePerHour != null ? ` · ${money(props.estimate.laborRatePerHour)} per labor hour` : ''}
                    </TableCell>
                  </TableRow>
                )}
                {pricingShown && props.estimate.laborCost != null && (
                  <TableRow>
                    <TableCell style={{ paddingLeft: 40 }}>
                      Labor{props.estimate.laborMinutes != null && props.estimate.laborMinutes > 0 ? ` · ${hoursAndMinutes(props.estimate.laborMinutes)} across the crew` : ''}
                    </TableCell>
                    <TableCell align="right"><PriceDisplay mode="read-only" value={props.estimate.laborCost} /></TableCell>
                  </TableRow>
                )}
                {pricingShown && props.estimate.equipmentCost != null && (
                  <TableRow>
                    <TableCell style={{ paddingLeft: 40 }}>
                      Equipment{(props.estimate.equipmentMinimumCharge ?? 0) > 0 ? ` · includes ${money(props.estimate.equipmentMinimumCharge!)} of minimum charges` : ''}
                    </TableCell>
                    <TableCell align="right"><PriceDisplay mode="read-only" value={props.estimate.equipmentCost} /></TableCell>
                  </TableRow>
                )}
                {props.displayMode === 'admin' && (props.estimate.costItems ?? []).length > 0 && (
                  <TableRow>
                    <TableCell style={{ paddingLeft: 40, borderBottom: 'none' }} colSpan={2}>Additional cost items</TableCell>
                  </TableRow>
                )}
                {props.displayMode !== 'contractor' && (props.estimate.costItems ?? []).map(costItem => (
                  <TableRow key={`cost-item-${costItem.id}`}>
                    <TableCell component="th" scope="row" style={props.displayMode === 'admin' ? { paddingLeft: 56, fontSize: '0.9rem' } : undefined}>
                      {costItem.description || 'Additional cost'}
                    </TableCell>
                    <TableCell align="right">
                      <PriceDisplay
                        mode={lineItemCostInputMode === 'hidden' ? 'hidden' : 'read-only'}
                        value={costItem.price}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {pricingShown && props.estimate.pricingRules === null && (props.estimate.laborCost != null) && (
                  <TableRow>
                    <TableCell style={{ paddingLeft: 40, fontSize: '0.9rem', borderBottom: 'none' }} colSpan={2}>
                      No pricing rules on this record (it predates them); nothing is added below.
                    </TableCell>
                  </TableRow>
                )}
                {/* SQ-441: a machine whose time comes to less than its minimum
                    charge is topped up once per job; the line says so (the
                    total already includes it). Line-item prices are the
                    admin's view, so this is too. */}
                {/* On a snapshot without the equipment sum (older invoices) the minimum top-up still gets its own line. */}
                {pricingShown && props.estimate.equipmentCost == null && (props.estimate.equipmentMinimumCharge ?? 0) > 0 && (
                  <TableRow>
                    <TableCell component="th" scope="row">Equipment minimum charge</TableCell>
                    <TableCell align="right">
                      <PriceDisplay mode="read-only" value={props.estimate.equipmentMinimumCharge!} />
                    </TableCell>
                  </TableRow>
                )}
                {/* SQ-283: the company's pricing rules, each as the amount ADDED
                    (never a replacement total, so the rows still sum to the
                    Total), in the ticket's order and only when that rule
                    applied — a normal sized local job shows none. Internal
                    only, like Android's customer view. */}
                {pricingShown && ([
                  ['Added for small job rate', props.estimate.smallJobRateAmount ?? 0],
                  ['Added to meet job minimum', props.estimate.minimumTopUpAmount ?? 0],
                  ['Added for travel', props.estimate.travelAmount ?? 0],
                ] as const).filter(([, amount]) => amount > 0).map(([label, amount]) => (
                  <TableRow key={`pricing-rule-${label}`}>
                    <TableCell component="th" scope="row">{label}</TableCell>
                    <TableCell align="right">
                      <PriceDisplay mode="read-only" value={amount} />
                    </TableCell>
                  </TableRow>
                ))}
                {/* SQ-636: no Total row where the prices were hidden from the person. */}
                {props.displayMode !== 'contractor' && !pricesHidden && <TableRow>
                    <TableCell component="th" scope="row" style={{ fontWeight: 'bold', fontSize: '1.4rem' }}>Total</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold', fontSize: '1.4rem' }}>
                      {totalCostInputMode === 'editable' && (rate != null || noCrew) ? (
                        <TimeStepperPrice
                          laborMinutes={calculateTotalLaborMinutes(props.estimate.trees)}
                          crewSize={crewSize}
                          noCrew={noCrew}
                          price={props.estimate.totalCost}
                          onStep={onTotalTimeStep}
                          variant="h6"
                          loading={props.isLoading}
                        />
                      ) : (
                        <PriceDisplay
                          mode={totalCostInputMode === 'hidden' ? 'hidden' : 'read-only'}
                          value={props.estimate.totalCost}
                        />
                      )}
                    </TableCell>
                </TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          {props.displayMode !== 'contractor' &&
          <div>
            <Typography variant="body2" sx={{ marginTop: 2 }}>
              Thank you for choosing our services. Please contact us for any further inquiries.
            </Typography>
            {props.displayType === 'estimate' &&
            <Typography variant="body2" sx={{ marginTop: 2 }}>
                This estimate is valid for 30 days.
            </Typography>}
            {props.displayType === 'invoice' &&
            <>
              <Typography variant="body2" sx={{ marginTop: 2 }}>
                  Make payable to: {props.company.name}
              </Typography>
              <Typography variant="body2" sx={{ marginTop: 2 }}>
                  Address: {props.company.billingStreetAddress1} {props.company.billingStreetAddress2 ?? ''} {props.company.billingCity}, {props.company.billingState}, {props.company.billingPostalCode}
              </Typography>
            </>}
            {props.displayType === 'receipt' &&
            <Typography variant="body2" sx={{ marginTop: 2 }}>
                Paid
            </Typography>}
          </div>}
        </div>
      </Paper>
      <PhotoViewer
        photos={(viewing?.photos ?? []).map(photo => ({id: photo.id, url: photo.imageUrl}))}
        startIndex={viewing?.index ?? null}
        onClose={() => setViewing(null)}
      />
    </div>
  );
})
